import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import Badge from '../../components/Badge';
import Button from '../../components/Button';
import Card from '../../components/Card';
import MetaList, { MetaListItem } from '../../components/MetaList';
import dockyBlank from '../../images/docky-blank.svg';
import { UIActions } from '../../ui/uiSlice';
import * as styles from './AdminApiToken.module.scss';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import { gql } from '../../graphql/generated/gql';

const ADMIN_API_TOKEN = gql(/** @complexity 1 */ `
  query adminApiToken($id: ID!) {
    user(id: $id) {
      id
      apiToken
    }
  }
`);

const REGENERATE_USER_API_TOKEN = gql(/** @complexity 2 */ `
  mutation regenerateUserApiToken($id: ID!) {
    regenerateUserApiToken(id: $id)
  }
`);

type AdminApiTokenProps =
  | {
      id: string;
      name: string;
      fakeToken?: undefined;
    }
  | { id?: undefined; name: string; fakeToken: string };

const AdminApiToken = (props: AdminApiTokenProps) => {
  const { id, name } = props;
  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const [tokenRevealed, setTokenRevealed] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const { data } = useQuery(ADMIN_API_TOKEN, {
    variables: { id: id! },
    skip: !id || !tokenRevealed,
  });

  const [regenerateToken] = useMutation(REGENERATE_USER_API_TOKEN);

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const useFake = !data && !props.id;
  const showToken = tokenRevealed && data?.user?.apiToken;

  const onRegenerateToken = () => {
    if (!id) {
      return;
    }
    regenerateToken({
      variables: { id },
      update: (proxy, result) => {
        const currentData = proxy.readQuery({
          query: ADMIN_API_TOKEN,
          variables: { id },
        });

        if (currentData?.user) {
          proxy.writeQuery({
            query: ADMIN_API_TOKEN,
            variables: { id },
            data: {
              user: {
                ...currentData?.user,
                apiToken: result.data?.regenerateUserApiToken,
              },
            },
          });
        }
      },
    });
  };

  const handleCopyButtonClick = async () => {
    if (data?.user?.apiToken) {
      await navigator.clipboard.writeText(data.user.apiToken);
      dispatch(
        UIActions.pushToast({
          level: 'success',
          message: 'Token copied',
        }),
      );
    }
  };

  return (
    <Card noPadding>
      <div
        className={classNames(styles.wrapper, {
          [styles.fakeWrapper]: useFake,
        })}
      >
        <div className={styles.img}>
          <img src={dockyBlank} height="16" alt="Slab Logo" />
        </div>
        <div
          className={classNames(styles.content, {
            [styles.tokenRevealed]: showToken,
          })}
        >
          <div className={styles.nameWrapper}>
            <div className={styles.name}>{name}</div>
            <Badge className={styles.badge} color="pink" inline={false}>
              BOT
            </Badge>
          </div>
          {useFake ? (
            <div className={styles.subtitle}>{props.fakeToken}</div>
          ) : (
            <>
              {!showToken ? (
                <Button
                  variant="secondary"
                  onClick={() => setTokenRevealed(true)}
                >
                  Reveal token
                </Button>
              ) : (
                <div className={styles.tokenWithControls}>
                  {data?.user?.apiToken}
                  <MetaList>
                    <MetaListItem>
                      <Button variant="link" onClick={handleCopyButtonClick}>
                        Copy
                      </Button>
                    </MetaListItem>
                    <MetaListItem>
                      <Button
                        variant="link"
                        onClick={() => onRegenerateToken()}
                      >
                        Regenerate
                      </Button>
                    </MetaListItem>
                  </MetaList>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AdminApiToken;
